import React from 'react';
import Box from '@material-ui/core/Box';
import CircularProgress from '@material-ui/core/CircularProgress';
import Typography from '@material-ui/core/Typography';
import { AssessmentStageBuilderItem } from '../AssessmentStage/AssessmentStageBuilder/assessment-stage-builder-item.component';
import { StageSubmitRegistrar } from '../_core/Formik/formik-submit-registrar.component';
import { IntlMsg } from '../_core/IntlMsg/intl-msg.component';
import { IFormOption } from '../_core/_ui/forms.component';
import { pluralize } from '../../lib/helpers/text.helpers';
import { messages } from './format-template.messages';

const noop = () => {};

interface IFormatTemplateStageListProps {
  stages: IAssessmentStage[];
  drillOptions: IFormOption[];
  practicePlanOptions: IFormOption[];
  positionOptions: IFormOption[];
  loading: boolean;
  // editable: render the stages' config as live inputs and route edits through updateStage;
  // otherwise render read-only (preview). updateStage is required when editable.
  editable?: boolean;
  updateStage?: (id: string, changes: { config?: object; title?: string }) => void;
  // Catalog stage settings: seed-owned inputs (title, per-band derived fields) hide.
  catalogSettingsMode?: boolean;
  // Optional per-stage adornment rendered above each stage (e.g. a catalog edit's band reach).
  renderStageAnnotation?: (stage: IAssessmentStage, index: number) => React.ReactNode;
  // Lets a parent-level Save flush stage forms whose own dirty-gated buttons are out of view.
  registerStageSubmit?: StageSubmitRegistrar;
}

// Accordion view of a template's stages, rendered with the shared stage builder items so GROUP/ROUTE
// stages resolve position names and SESSION stages show drills. Read-only preview or editable config.
export const FormatTemplateStageList: React.FC<IFormatTemplateStageListProps> = ({
  stages,
  drillOptions,
  practicePlanOptions,
  positionOptions,
  loading,
  editable = false,
  updateStage,
  catalogSettingsMode,
  renderStageAnnotation,
  registerStageSubmit,
}) => {
  let sessionNum = 0;

  return (
    <>
      <Typography variant="subtitle2">
        <IntlMsg {...messages.stageCount} values={{ count: stages.length, stageWord: pluralize(stages.length, 'stage') }} />
      </Typography>
      {loading && stages.length === 0 && <Typography><IntlMsg {...messages.loading} /></Typography>}
      {!loading && stages.length === 0 && (
        <Typography color="textSecondary" variant="body2"><IntlMsg {...messages.noStages} /></Typography>
      )}
      {loading && stages.length > 0 && (
        <Box display="flex" alignItems="center" mt={1}>
          <CircularProgress size={16} style={{ marginRight: 8 }} />
          <Typography variant="caption" color="textSecondary"><IntlMsg {...messages.loadingDetails} /></Typography>
        </Box>
      )}
      {stages.map((stage, index) => {
        if (stage.type === 'SESSION') sessionNum += 1;
        return (
          <React.Fragment key={stage._id}>
          {renderStageAnnotation?.(stage, index)}
          <AssessmentStageBuilderItem
            previewMode={!editable}
            templateEditMode={editable}
            catalogSettingsMode={catalogSettingsMode}
            registerStageSubmit={registerStageSubmit}
            sessionNum={sessionNum}
            stage={stage}
            stages={stages}
            index={index}
            ageGroup={null}
            drillOptions={drillOptions}
            practicePlanOptions={practicePlanOptions}
            positionOptions={positionOptions}
            completeStage={noop}
            createStage={noop}
            updateStage={editable && updateStage ? updateStage : noop}
            removeStage={noop}
            selectionMode={false}
            isSelected={false}
            isSelectionDisabled={false}
            onToggleSelect={noop}
          />
          </React.Fragment>
        );
      })}
    </>
  );
};
